<template>
  <div :class="['cpt-chat-img', {'img-info-block': img_info && img_info.width}]">
    <img 
      class="preload-img" 
      :src="`data:image/svg+xml;charset=UTF-8,<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; width=&quot;${img_info.width}&quot; height=&quot;${img_info.height}&quot; viewBox=&quot;0 0 894 536&quot;><rect opacity=&quot;0&quot; width=&quot;894&quot; height=&quot;536&quot;/></svg>`" 
      alt=""
      v-if="img_info && img_info.width"
    >
    <img
      ref="img-dom"
      :class="[
        'img-dom', 
        `img-fit-${fit}`, 
        {
          'preview': canPreview, 
          'lazy': lazy, 
          'animation': animation,
          'opacity': animation
        }
      ]"
      alt="image"
      @click="openImage"
      @error="$emit('error')"
      @load="handleLoad"
      v-show="!hidden_gif"
      :src="(!lazy && !animation) ? real_src : PRELOAD_IMG"
    >
  </div>
</template>

<script>
import { setImgSrc, formatWebplImg, formatGifFrame, formatThumbnailImg } from '@/ChatUI/utils/img-lazy-load'
import EventBus from '_lib/EventBus'

const PRELOAD_IMG = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=='

export default {
  name: 'ChatImg',
  props: {
    src: { // 图片链接
      type: String,
      default: ''
    },
    lazy: { // 是否开启懒加载
      type: Boolean,
      default: true
    },
    animation: { // 是否展示懒加载动画
      type: Boolean,
      default: true,
    },
    webp_quality: { // 转换webp
      type: Number,
      default: 100
    },
    preview_list: { // 预览列表
      type: Array,
      default: () => []
    },
    fit: { // 图片渲染方式，同object-fit
      type: String,
      default: 'cover'
    },
    img_info: { // 图像信息，存高宽
      type: Object,
      default: () => {}
    },
    thumbnail: { // 缩放操作, 参考https://cloud.tencent.com/document/product/460/36540
      type: String,
      default: ''
    },
    backend_gif_pause: {
      type: Boolean,
      default: true
    },
    gif_active_type: { // 激活gif的类型 ['hover', 'all', 'focus', 'selected']
      type: String,
      default: 'focus'
    },
    hover_dom_handler: {
      default: null,
    },
    error: {
      default: true,
      type: Boolean,
    },
    isActive: {
      default: false,
      type: Boolean,
    },
  },
  computed: {
    initialPreviewIndex() {
      let index = this.preview_list.indexOf(this.img_info?.url)
      if (index !== -1) return index
      
      return this.preview_list.indexOf(this.src)
    },
    canPreview () {
      return this.preview_list.length > 0 && this.src
    },
    real_src () {
      let url = this.src
      if (this.webp_quality > 0 && this.webp_quality < 100) {
        url = formatWebplImg(url, this.webp_quality)
      }
      if (this.is_gif && this.gif_active_type !== 'all' && !this.is_git_active) {
        url = formatGifFrame(url)
      }
      if (this.thumbnail) {
        url = formatThumbnailImg(url, this.thumbnail)
      }
      return url
    },
    is_gif () {
      return /\.gif$/i.test(this.src)
    },
  },
  data () {
    return {
      showViewer: false,
      hidden_gif: false,
      windowShowStateWatcher: null, // 用于存储停止观察函数
      is_git_active: false, // 是否激活gif
      PRELOAD_IMG,
    }
  },
  mounted () {
    this.ele = this.$refs['img-dom']
    this.init()
  },
  beforeDestroy() {
    this.releaseGifListener()
  },
  activated () {
    this.initGifListener()
  },
  deactivated () {
    this.changeGifState(false)
    this.releaseGifListener()
  },
  watch: {
    real_src () {
      this.init()
    },
    isActive: {
      handler (newVal) {
        if (this.gif_active_type !== 'selected') {
          return
        }
        this.changeGifState(newVal)
      },
      immediate: true
    }
  },
  methods: {
    init() {
      this.ele.setAttribute('data-original', this.real_src)
      this.ele.setAttribute('data-allow-error', this.error ? 1 : 0)
      this.handleImg()
      if (this.is_gif) {
        this.initGifListener()
      }
    },
    openViewer() {
      EventBus.$emit('openImgPreviewer', this.preview_list, this.initialPreviewIndex)
    },
    handleImg () {
      if (!this.lazy && !this.animation) {
        return
      }
      let ele_src = this.ele.getAttribute('src')
      if (this.lazy && (!ele_src || ele_src === PRELOAD_IMG)) {
        window.hcImg.observer.observe(this.ele)
      } else {
        setImgSrc(this.ele)
      }
    },
    openImage () {
      if (this.canPreview) {
        if (this.preview_list.length == 0) {
          this.preview_list = [this.src]
        }
        this.openViewer()
      }
    },
    initGifListener() {
      this.releaseGifListener()
      if (['hover', 'selected'].includes(this.gif_active_type)) {
        this._listeners = [{
          dom: (this.hover_dom_handler && this.hover_dom_handler()) || this.$el,
          event: 'mouseenter',
          func: () => {
            this.changeGifState(true)
          }
        }, {
          dom: (this.hover_dom_handler && this.hover_dom_handler()) || this.$el,
          event: 'mouseleave',
          func: () => {
            this.changeGifState(false)
          }
        }]
      } else if (this.gif_active_type === 'focus') {
        this._listeners = [{
          dom: window,
          event: 'focus',
          func: () => {
            this.changeGifState(true)
          }
        }, {
          dom: window,
          event: 'blur',
          func: () => {
            this.changeGifState(false)
          }
        }]
        if (document.hasFocus()) {
          this.changeGifState(true)
        }
      }
      this._listeners?.forEach(({ dom, event, func }) => {
        dom.addEventListener(event, func)
      })
    },
    releaseGifListener() {
      this._listeners?.forEach(({ dom, event, func }) => {
        dom.removeEventListener(event, func)
      })
    },
    changeGifState(bl) {
      const oldVal = this.is_git_active
      let newVal
      if (this.gif_active_type === 'selected') {
        newVal = bl || this.isActive
      } else {
        newVal = bl
      }
      if(oldVal === newVal) return;
      this.is_git_active = newVal
      this.ele.setAttribute('data-original', this.real_src)
      setImgSrc(this.ele)
    },
    handleLoad(event) {
      this.$emit('load', event)
    }
  },
}
</script>
<style lang="scss">
.cpt-chat-img {
  height: 100%;
  width: 100%;
  border-radius: inherit;
  &.img-info-block {
    position: relative;
    .img-dom {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
    .preload-img {
      width: 100%;
      height: 100%;
    }
  }
  .img-dom {
    display: block;
    object-fit: cover;
    border-radius: inherit;
    pointer-events: auto;
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    &.opacity {
      opacity: 0;
    }
    &.animation {
      transition: opacity .5s cubic-bezier(0.25, 0.1, 0.25, 1);
    }
    &.preview {
      cursor: zoom-in;
    }
    &.img-fit {
      &-cover {
        object-fit: cover;
      }
      &-contain {
        object-fit: contain;
      }
      &-fill {
        object-fit: fill;
      }
      &-scale-down {
        object-fit: scale-down;
      }
      &-none {
        object-fit: none;
      }
    }
    &[data-error] {
      object-fit: cover;
    }
  }
}
</style>